import type { ReactNode } from 'react';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { isEmpty, throttle } from 'lodash';

import SafeComponent from '~/components/core/SafeComponent';
import cn from '~/Utils/cn';

import CollapsibleWrapper from '../../Collapsible/CollapsibleWrapper';
import { Heading, Text } from '../../TextComponents';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import styles from './FsIndexPanel.module.scss';

// A non-collapsible titled band grouping related section cards in the main panel. Tailwind, not a .scss
// module rule (repo convention). The headline gap lives on the title's `mb-20`: MUI Typography resets a
// margin set via a single-class rule, but a Tailwind utility emits as `body .mb-20` (specificity 0,1,1)
// and wins — so the gap can sit on the title here, unlike the .scss version which had to move it below.
const GroupedSectionsCard = ({ title, children }: { title: string; children: ReactNode }) => (
  <div
    data-testid="grouped-sections-card"
    className="rounded-lg border border-s-[4px] border-solid border-slate-500 border-s-teal-700 bg-green-100 px-20 py-16"
  >
    <Text
      variant={Text.VARIANTS.SM}
      weight={Text.WEIGHTS.SEMI_BOLD}
      colorVariant={Text.COLOR_VARIANTS.BUTTON_LINK}
      className="mb-20 block uppercase tracking-[0.06em]"
    >
      {title}
    </Text>
    <div className="grid gap-16">{children}</div>
  </div>
);

export interface IndexPanelSection {
  id: string | number;
  // Optional: a group section carries only `children` and no node of its own.
  node?: ReactNode;
  title: string;
  isHidden?: boolean;
  isCollapsible?: boolean;
  // Render this group's body as one non-collapsible titled band wrapping its children (which keep
  // their own scroll anchors), instead of flattening the children into top-level sibling cards.
  renderBodyAsBand?: boolean;
  // Opt-in override for how a band's children are laid out, so a caller can add behaviour this
  // component has no business knowing about (drag-reordering, which needs feature-level components
  // core must not import). Omit it and the body renders exactly as it always has.
  //
  // `renderChild` is handed back rather than left to the caller so each child keeps its scroll
  // anchor and its own SafeComponent boundary however the caller arranges them.
  renderBandBody?: (args: {
    children: IndexPanelSection[];
    renderChild: (child: IndexPanelSection) => ReactNode;
  }) => ReactNode;
  children?: IndexPanelSection[];
}

interface FsIndexPanelProps {
  panelTitle: string;
  sections: IndexPanelSection[];
  SectionWrapperComponent: React.FC;
  sectionContainerClassName?: string;
  handleScrollTopOffset: number;
  withLeftOffset?: boolean;
}

const WAIT_MS = 200;

const FsIndexPanel: React.FC<FsIndexPanelProps> = ({
  panelTitle,
  sections,
  SectionWrapperComponent = Fragment,
  sectionContainerClassName,
  handleScrollTopOffset = 0,
  withLeftOffset = true,
}) => {
  const [activeSection, setActiveSection] = useState(sections[0].id);

  const sectionRefs = useRef<{ [sectionId: string]: HTMLElement | null }>({});

  const handleScroll = useCallback(() => {
    Object.keys(sectionRefs.current).find((sectionId) => {
      const element = sectionRefs.current[sectionId];
      if (element) {
        const rect = element.getBoundingClientRect();

        const bottomShows = rect.top < window.innerHeight && rect.bottom >= 0;
        const topShows = rect.top >= handleScrollTopOffset && rect.top <= window.innerHeight;

        if (bottomShows && topShows) {
          setActiveSection(sectionId);
          return bottomShows && topShows;
        }
      }
    });
  }, [handleScrollTopOffset]);

  useEffect(() => {
    // this listener creates dual-dependency on the scroll event - throttle handleScroll
    const fn = throttle(handleScroll, WAIT_MS);
    window.addEventListener('scroll', fn, true);
    return () => {
      window.removeEventListener('scroll', fn, true);
    };
  }, [handleScroll]);

  const scrollToSection = (sectionId: string | number) => {
    if (sectionRefs.current[sectionId]) {
      sectionRefs.current[sectionId]?.scrollIntoView({
        behavior: 'smooth',
      });
      setActiveSection(sectionId);
    }
  };

  const sectionsToShowOnIndex = sections.filter(({ isHidden }) => !isHidden);

  const setSectionRef = (id: string | number) => (ref: HTMLElement | null) => {
    sectionRefs.current[id] = ref;
  };

  // A standard scroll-anchored card: one section wrapped in the caller's section wrapper.
  const renderSectionCard = (section: IndexPanelSection | undefined) => {
    if (!section) return null;
    const { node, id } = section;
    return (
      <div className={sectionContainerClassName} key={id} ref={setSectionRef(id)}>
        <SectionWrapperComponent>{node}</SectionWrapperComponent>
      </div>
    );
  };

  return (
    <div className={styles.indexPanelWrapper}>
      <div className="w-full">
        {sectionsToShowOnIndex.map((section) => {
          if (!section) return null;

          const { id, title, children, renderBodyAsBand, renderBandBody } = section;

          // Band group: one non-collapsible titled tinted container holding the children's cards,
          // each keeping its own scroll anchor so the nav sub-entries still scroll to their card.
          // Each child gets its own SafeComponent boundary so one throwing section blanks only its
          // own card, matching the per-child isolation the flattened path gets from renderSectionCard.
          // (SafeComponent, not SectionWrapper — the latter also carries band-breaking layout.)
          if (renderBodyAsBand && !isEmpty(children)) {
            const renderChild = (child: IndexPanelSection) => (
              <div className={sectionContainerClassName} key={child.id} ref={setSectionRef(child.id)}>
                <SafeComponent>{child.node}</SafeComponent>
              </div>
            );
            return (
              <SectionWrapperComponent key={id}>
                <GroupedSectionsCard title={title}>
                  {renderBandBody
                    ? renderBandBody({ children: children ?? [], renderChild })
                    : children?.map(renderChild)}
                </GroupedSectionsCard>
              </SectionWrapperComponent>
            );
          }

          // Non-band group → flatten children to top-level cards; leaf → the section itself.
          return (isEmpty(children) ? [section] : (children ?? [])).map(renderSectionCard);
        })}
      </div>
      <div className={cn(styles.indexPanel, { [styles.withLeftOffset]: withLeftOffset })}>
        <Heading variant={Heading.TYPES.H3} className={styles.heading}>
          {panelTitle}
        </Heading>
        <div className={styles.sectionsIndexes}>
          {sectionsToShowOnIndex.map(({ title, id, children = [] }) => (
            <Fragment key={id}>
              {isEmpty(children) ? (
                <div
                  onClick={() => scrollToSection(id)}
                  className={cn(styles.index, { [styles.activeIndex]: activeSection === id })}
                >
                  <Text
                    variant={Text.VARIANTS.SM}
                    colorVariant={Text.COLOR_VARIANTS.SECONDARY}
                    className={styles.sectionTitle}
                  >
                    {title}
                  </Text>
                </div>
              ) : (
                <CollapsibleWrapper
                  chevronSide={CollapsibleWrapper.CHEVRON_SIDE.RIGHT}
                  variant={CollapsibleWrapper.VARIANT.slim}
                  summarySectionClassname={styles.index}
                  title={
                    <div>
                      <Text
                        variant={Text.VARIANTS.SM}
                        colorVariant={Text.COLOR_VARIANTS.SECONDARY}
                        className={styles.sectionTitle}
                      >
                        {title}
                      </Text>
                    </div>
                  }
                  key={id}
                  noBorder
                  noDivider
                  withActionsContainerFullWidth={false}
                >
                  {children
                    .filter((child) => !child.isHidden)
                    .map(({ title, id }) => (
                      <div
                        key={id}
                        onClick={() => scrollToSection(id)}
                        className={cn(styles.index, {
                          [styles.collapseChild]: true,
                          [styles.activeIndex]: activeSection === id,
                        })}
                      >
                        <Text
                          variant={Text.VARIANTS.SM}
                          colorVariant={Text.COLOR_VARIANTS.SECONDARY}
                          className={styles.sectionTitle}
                        >
                          {title}
                        </Text>
                      </div>
                    ))}
                </CollapsibleWrapper>
              )}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FsIndexPanel;
